import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Button,
  Container,
  TextField,
  Table,
  Modal,
  TableBody,
  TableCell,
  Box,
  Typography,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  styled,
  InputAdornment,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  IconButton,
  useTheme,
  Chip,
} from '@mui/material';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  KeyboardDoubleArrowLeft as ChevronLeft,
  KeyboardDoubleArrowRight as ChevronRight,
  Close as CloseIcon,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';
import { useBranch } from '../../../layouts/BranchContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

/* ── Shared axios instance with token interceptor ────────────────────────── */
const api = axios.create({ baseURL: `${API_BASE_URL}/api` });

const getAccessToken = () => {
  const keys = ['accessToken', 'access_token', 'token'];
  for (const key of keys) {
    const local = localStorage.getItem(key);
    if (local) return local;
    const session = sessionStorage.getItem(key);
    if (session) return session;
  }
  return null;
};

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const getStoredUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem('user') || localStorage.getItem('user') || 'null');
  } catch (e) {
    return null;
  }
};

const getArray = (response) => {
  const data = response?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const Certificate = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  /* ── Global branch from BranchContext ── */
  const { selectedBranchId } = useBranch();

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [showUserGuideModal, setShowUserGuideModal] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [courses, setCourses] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  /* ── Organization from stored user ── */
  const [organizationId, setOrganizationId] = useState('');

  useEffect(() => {
    const user = getStoredUser();
    const orgId = user?.organization_id || user?.organization?.id || user?.organization || '';
    setOrganizationId(orgId ? String(orgId) : '');
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleAuthError = (error) => {
    if (error.response?.status === 401) {
      ['accessToken', 'access_token', 'token'].forEach((k) => {
        localStorage.removeItem(k);
        sessionStorage.removeItem(k);
      });
      navigate('/login');
    }
  };

  // GET /api/course/courses/ — branch-scoped
  const fetchCourses = async () => {
    try {
      const params = {};
      if (organizationId) params.organization = Number(organizationId);
      if (selectedBranchId) params.branch = Number(selectedBranchId);
      const response = await api.get('/course/courses/', { params });
      setCourses(getArray(response));
    } catch (error) {
      console.error('Error fetching courses:', error);
      showSnackbar('Failed to fetch courses', 'error');
      handleAuthError(error);
    }
  };

  // GET /api/admission/certificate-template/ — branch-scoped
  // Supports: ?organization=1 | ?branch=10 | ?organization=1&branch=10
  const fetchCertificates = async () => {
    try {
      const params = {};
      if (organizationId) params.organization = Number(organizationId);
      if (selectedBranchId) params.branch = Number(selectedBranchId);
      const response = await api.get('/admission/certificate-template/', { params });
      const list = getArray(response);
      setCertificates(list);
      setFilteredData(list);
    } catch (error) {
      console.error('Error fetching certificates:', error);
      showSnackbar('Failed to fetch certificates', 'error');
      handleAuthError(error);
    }
  };

  useEffect(() => {
    fetchCertificates();
    fetchCourses();
  }, [organizationId, selectedBranchId, navigate]);

  // ── Delete Certificate Template ────────────────────────────────────────────
  // DELETE /api/admission/certificate-template/<certificateId>/
  // Response 200: { message } | 204: no body (backend soft-deletes via is_active=False)
  const handleDeleteCertificate = async (certificateId) => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      const response = await api.delete(`/admission/certificate-template/${certificateId}/`);
      if (response.status === 200 || response.status === 204) {
        const newList = certificates.filter((cert) => cert.id !== certificateId);
        setCertificates(newList);
        setFilteredData((prev) => prev.filter((cert) => cert.id !== certificateId));
        showSnackbar('Certificate template deleted successfully!');
        const newTotalPages = Math.ceil(newList.length / rowsPerPage);
        if (page > newTotalPages && newTotalPages > 0) setPage(newTotalPages);
        else if (newList.length === 0) setPage(1);
      }
    } catch (error) {
      console.error('Error deleting certificate:', error);
      showSnackbar(
        error.response?.data?.message || 'Failed to delete certificate template',
        'error'
      );
      handleAuthError(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConfirmDelete = () => {
    const idToDelete = selectedCertificate.id;
    setShowDeleteModal(false);
    setSelectedCertificate(null);
    handleDeleteCertificate(idToDelete);
  };

  // ── Add / Edit now live on separate full-page designers ──
  const handleEdit = (certificate) => {
    navigate(`/edit-certificate/${certificate.id}`);
  };

  const handleDelete = (certificate) => {
    setSelectedCertificate(certificate);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedCertificate(null);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  // Search filter by template name
  useEffect(() => {
    const filtered = certificates.filter((certificate) =>
      certificate.template_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredData(filtered);
    setPage(1);
  }, [searchTerm, certificates]);

  const paginatedData = filteredData.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  return (
    <Container maxWidth={false} sx={{ width: '100%' }}>
      {/* ── Top bar ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, p: 2, boxShadow: 'none', maxWidth: '100%', boxSizing: 'border-box' }}>
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Search */}
          <TextField
            variant="outlined"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.palette.primary.main, marginLeft: 1.5, fontSize: 24 }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')} sx={{ color: 'grey', marginRight: 1 }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: '30px',
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : '#fff',
                boxShadow: theme.palette.mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
                '& .MuiOutlinedInput-notchedOutline': { border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'}` },
                '&:hover': { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(240,240,240,1)', boxShadow: theme.palette.mode === 'dark' ? '0 2px 12px rgba(0,0,0,0.4)' : '0 2px 12px rgba(0,0,0,0.15)' },
                '&.Mui-focused': { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.25)' : '#fff', boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`, '& .MuiOutlinedInput-notchedOutline': { border: `2px solid ${theme.palette.primary.main}` } },
                paddingLeft: 1.5,
                height: '50px',
                transition: 'all 0.3s ease',
              },
            }}
            sx={{ width: 350, maxWidth: '100%', '& .MuiInputBase-input': { padding: '10px 14px', color: theme.palette.text.primary, fontWeight: 500 } }}
          />

          <Button
            variant="contained"
            onClick={() => navigate('/add-certificate')}
            sx={{ ml: 2, width: '200px', height: '50px', borderRadius: '10px', padding: '10px 0', fontWeight: 'bold', backgroundColor: theme.palette.primary.main, '&:hover': { backgroundColor: theme.palette.primary.dark } }}
            startIcon={<AddIcon />}
          >
            ADD TEMPLATE
          </Button>
        </Box>

        <Tooltip title="User Guide">
          <IconButton onClick={() => setShowUserGuideModal(true)} sx={{ width: 60, height: 60, borderRadius: '50%', minWidth: 0 }}>
            <InfoOutlineIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Table ── */}
      <Paper elevation={3} sx={{ paddingBottom: 0, maxWidth: '100%', boxSizing: 'border-box', mx: 'auto', backgroundColor: theme.palette.background.paper, borderRadius: '16px' }}>
        <CustomTableScrollbar sx={{ width: '100%', overflowX: 'auto' }}>
          <TableContainer sx={{ width: '100%', borderRadius: '16px' }}>
            <Table sx={{ '& .MuiTableHead-root .MuiTableCell-root': { fontWeight: 'bold', backgroundColor: theme.palette.primary.main, color: 'white' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem', minWidth: 70, whiteSpace: 'nowrap' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem', minWidth: 250, whiteSpace: 'nowrap' }}>Course</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem', minWidth: 300, whiteSpace: 'nowrap' }}>Template Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem', minWidth: 200, whiteSpace: 'nowrap' }}>Institute Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem', minWidth: 120, whiteSpace: 'nowrap' }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.dark, fontSize: '1rem', minWidth: 120, whiteSpace: 'nowrap' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((certificate, index) => {
                    const courseName = certificate.course_name || courses.find((c) => c.id === certificate.course)?.course_name || `Course ID: ${certificate.course}`;
                    return (
                      <TableRow
                        key={certificate.id}
                        sx={{ backgroundColor: index % 2 === 0 ? theme.palette.background.paper : theme.palette.action.hover, '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}
                      >
                        <TableCell sx={{ fontSize: '0.95rem', whiteSpace: 'nowrap' }}>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                        <TableCell sx={{ fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                          <Chip label={courseName} size="small" color="primary" variant="outlined" sx={{ fontWeight: 500 }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                          <Chip label={certificate.template_name || '-'} size="small" color="secondary" variant="outlined" />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                          {certificate.institute_name || '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                          <Chip label={certificate.is_active ? 'Active' : 'Inactive'} size="small" color={certificate.is_active ? 'success' : 'default'} variant="outlined" />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit Template">
                            <IconButton onClick={() => handleEdit(certificate)} sx={{ color: theme.palette.primary.light, '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }} size="small">
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Template">
                            <IconButton color="error" onClick={() => handleDelete(certificate)} sx={{ '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }} size="small">
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ fontSize: '1rem', padding: '20px' }}>
                      No Data Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CustomTableScrollbar>
      </Paper>

      {/* ── Pagination ── */}
      {filteredData.length > 0 && (
        <Box sx={{ maxWidth: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, p: 2, mx: 'auto', pt: 2 }}>
          <Typography variant="body2" color={theme.palette.primary.main}>
            Page <strong>{page}</strong> of <strong>{Math.ceil(filteredData.length / rowsPerPage)}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton onClick={() => handleChangePage(null, page - 1)} disabled={page === 1} sx={{ color: theme.palette.primary.main, '&:hover': { backgroundColor: theme.palette.action.hover }, '&.Mui-disabled': { opacity: 0.3 }, borderRadius: '50%', width: '36px', height: '36px' }}>
              <ChevronLeft />
            </IconButton>
            {[...Array(Math.ceil(filteredData.length / rowsPerPage))].map((_, index) => (
              <IconButton
                key={index + 1}
                onClick={() => handleChangePage(null, index + 1)}
                sx={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: page === index + 1 ? theme.palette.primary.main : 'transparent', color: page === index + 1 ? theme.palette.primary.contrastText : theme.palette.text.primary, '&:hover': { backgroundColor: page === index + 1 ? theme.palette.primary.dark : theme.palette.action.hover } }}
              >
                {index + 1}
              </IconButton>
            ))}
            <IconButton onClick={() => handleChangePage(null, page + 1)} disabled={page >= Math.ceil(filteredData.length / rowsPerPage)} sx={{ color: theme.palette.primary.main, '&:hover': { backgroundColor: theme.palette.action.hover }, '&.Mui-disabled': { opacity: 0.3 }, borderRadius: '50%', width: '36px', height: '36px' }}>
              <ChevronRight />
            </IconButton>
            <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
              <InputLabel id="rows-per-page-label" sx={{ color: theme.palette.text.primary }}>Rows</InputLabel>
              <Select labelId="rows-per-page-label" value={rowsPerPage} onChange={handleChangeRowsPerPage} label="Rows" sx={{ height: '36px', color: theme.palette.text.primary, '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main } }}>
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      {/* ── Snackbar ── */}
      <Snackbar open={openSnackbar} autoHideDuration={4000} onClose={() => setOpenSnackbar(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* ── Delete Confirmation Modal ── */}
      <Modal open={showDeleteModal} onClose={handleCloseDeleteModal} BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.8)' } }}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 400, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, overflow: 'hidden' }}>
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography variant="h6" sx={{ color: '#fff' }}>Delete Confirmation ⚠️</Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography sx={{ mb: 2 }}>
              Are you sure you want to delete the template <strong>"{selectedCertificate?.template_name}"</strong>?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ mr: 1 }} data-app-cancel="true">Cancel</Button>
              <Button variant="contained" color="error" onClick={handleConfirmDelete}>Yes, Delete</Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* ── User Guide Modal ── */}
      <Modal open={showUserGuideModal} onClose={() => setShowUserGuideModal(false)} BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.8)' } }}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 800, bgcolor: 'background.paper', borderRadius: '16px', boxShadow: 24, overflow: 'hidden' }}>
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>Certificate Management - User Guide</Typography>
            <IconButton onClick={() => setShowUserGuideModal(false)} sx={{ color: '#fff', position: 'absolute', right: 14 }}><CloseIcon /></IconButton>
          </Box>
          <CustomTableScrollbar sx={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}>➤ Follow these steps to add a new certificate template:</Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
              <li>Click the <strong>"ADD TEMPLATE"</strong> button — it opens the full-page <strong>Add Certificate</strong> designer.</li>
              <li>Select the <strong>Course</strong> and fill in the <strong>Template Name</strong>.</li>
              <li>Design the certificate live: drag & drop your logo, signature, stamp and background images, tweak fonts, colors and layout, and watch the preview update instantly.</li>
              <li>Use the <strong>"PREVIEW"</strong> button to see a preview with the server's sample data before saving.</li>
              <li>Click <strong>"SAVE"</strong> to create the template. If a template already exists for that branch, a confirmation dialog will ask if you want to overwrite it.</li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>⚠️ Note: All required fields must be filled. Template Name must be unique. Only one template per branch is allowed.</Typography>

            <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}>➤ Follow these steps to update an existing template:</Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
              <li>Click the <strong>Edit</strong> icon (pencil) in the Action column — it opens the full-page <strong>Edit Certificate</strong> designer.</li>
              <li>Update any fields as needed and click <strong>"UPDATE"</strong> to save.</li>
            </ul>

            <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}>➤ Follow these steps to delete a template:</Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
              <li>Click the <strong>Delete</strong> icon next to the template.</li>
              <li>Confirm by clicking <strong>"Yes, Delete"</strong> in the dialog.</li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>⚠️ Note: Deleted templates cannot be recovered.</Typography>

            <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}>➤ Available placeholders for Body Text:</Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
              <li><code>{'{{student_name}}'}</code> – Student's full name</li>
              <li><code>{'{{course_name}}'}</code> – Course name</li>
              <li><code>{'{{completion_date}}'}</code> – Completion/issue date</li>
              <li><code>{'{{batch_name}}'}</code> – Batch name</li>
            </ul>
          </CustomTableScrollbar>
        </Box>
      </Modal>
    </Container>
  );
};

export default Certificate;